import { useState, useMemo } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { LucideIcon, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  CalendarDays,
  ChevronRight,
  CalendarX2,
  AlertCircle,
  UserX,
  Clock,
  CreditCard,
  MessageCircle,
  Star,
  CheckCircle2,
  Activity,
  CalendarPlus,
  CalendarCheck,
  MessageSquare,
  DollarSign,
  UserPlus,
  TrendingUp,
  Home,
  Calendar,
  Menu,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// =============================================================================
// StatCard
// =============================================================================

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: LucideIcon;
  trend?: {
    value: number;
    direction: 'up' | 'down' | 'neutral';
  };
  variant?: 'default' | 'primary';
  sparklineData?: number[];
}

function MicroSparkline({ data, color }: { data: number[]; color: string }) {
  if (!data || data.length < 2) return null;

  const width = 60;
  const height = 20;
  const padding = 2;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const points = data.map((value, index) => {
    const x = padding + (index / (data.length - 1)) * (width - padding * 2);
    const y = height - padding - ((value - min) / range) * (height - padding * 2);
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} className="opacity-60">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  variant = 'default',
  sparklineData,
}: StatCardProps) {
  const isPrimary = variant === 'primary';

  return (
    <Card
      className={`relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:scale-[1.02] hover:shadow-xl ${
        isPrimary ? 'bg-gradient-to-br from-[#0D4F3C] to-[#238857] text-white' : ''
      }`}
    >
      {isPrimary && (
        <div className="absolute -right-6 -bottom-6 h-28 w-28 rounded-full bg-white/10" />
      )}

      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div
          className={`h-12 w-12 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${
            isPrimary
              ? 'bg-white/20'
              : 'bg-[#10B981]/10'
          }`}
        >
          <Icon
            className={`h-6 w-6 ${
              isPrimary ? 'text-white' : 'text-[#10B981]'
            }`}
          />
        </div>
        {trend && trend.direction !== 'neutral' && (
          <div
            className={`inline-flex items-center gap-0.5 text-xs font-medium px-2 py-1 rounded-full ${
              isPrimary
                ? trend.direction === 'up'
                  ? 'bg-white/20 text-white'
                  : 'bg-orange-400/30 text-orange-100'
                : trend.direction === 'up'
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-orange-100 text-orange-700'
            }`}
          >
            {trend.direction === 'up' ? (
              <ArrowUpRight className="h-3 w-3" />
            ) : (
              <ArrowDownRight className="h-3 w-3" />
            )}
            {Math.abs(trend.value)}%
          </div>
        )}
      </CardHeader>

      <CardContent className="relative">
        <div className="flex items-end justify-between gap-2">
          <div
            className={`text-3xl font-bold tracking-tight font-display ${
              isPrimary ? 'text-white' : 'text-foreground'
            }`}
          >
            {value}
          </div>
          {sparklineData && sparklineData.length >= 2 && (
            <MicroSparkline
              data={sparklineData}
              color={isPrimary ? 'rgba(255,255,255,0.7)' : '#10B981'}
            />
          )}
        </div>
        <p
          className={`text-sm font-medium mt-1 ${
            isPrimary ? 'text-white/90' : 'text-muted-foreground'
          }`}
        >
          {title}
        </p>
        <p
          className={`text-xs mt-0.5 ${
            isPrimary ? 'text-white/70' : 'text-muted-foreground/70'
          }`}
        >
          {subtitle}
        </p>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// TodaysSchedule
// =============================================================================

interface ScheduleBooking {
  id: string;
  serviceName: string;
  customerName: string;
  scheduledAt: Date | string;
  endsAt: Date | string;
  status: string;
}

interface TodaysScheduleProps {
  bookings: ScheduleBooking[];
  isLoading?: boolean;
}

export function TodaysSchedule({ bookings, isLoading }: TodaysScheduleProps) {
  const formatTime = (date: Date | string) => {
    const d = new Date(date);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const getMinutesUntil = (date: Date | string) => {
    const target = new Date(date);
    const now = new Date();
    const diffMs = target.getTime() - now.getTime();
    return Math.max(0, Math.round(diffMs / 60000));
  };

  const now = new Date();
  const maxVisibleItems = 4;
  const sortedBookings = [...bookings].sort(
    (a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
  );

  const nextBookingIndex = sortedBookings.findIndex(
    (b) => new Date(b.scheduledAt) > now
  );

  const statusDotColors: Record<string, string> = {
    PENDING: 'bg-amber-400',
    CONFIRMED: 'bg-emerald-400',
    IN_PROGRESS: 'bg-blue-400 animate-pulse',
    COMPLETED: 'bg-slate-300',
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold font-display flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-[#10B981]" />
            Today's Schedule
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-[#f3efe9] animate-pulse">
                <div className="w-14 h-10 bg-[#f3efe9] rounded" />
                <div className="h-10 w-px bg-[#f3efe9]" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-[#f3efe9] rounded w-3/4" />
                  <div className="h-3 bg-[#f3efe9] rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold font-display flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-[#10B981]" />
          Today's Schedule
        </CardTitle>
        <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground">
          <Link to="/operator/calendar" className="flex items-center gap-1">
            View all
            <ChevronRight className="h-4 w-4" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {sortedBookings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CalendarX2 className="h-10 w-10 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">No bookings scheduled for today</p>
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              {sortedBookings.slice(0, maxVisibleItems).map((booking, index) => {
                const isNext = index === nextBookingIndex;
                const isPast = new Date(booking.endsAt) < now;
                const minutesUntil = getMinutesUntil(booking.scheduledAt);

                return (
                  <Link
                    key={booking.id}
                    to={`/operator/bookings/${booking.id}` as string}
                    className={`flex items-center gap-2 py-2 px-2.5 rounded-lg border transition-all duration-200 group ${
                      isNext
                        ? 'border-[#10B981] bg-[#10B981]/5 hover:bg-[#10B981]/10'
                        : isPast
                        ? 'border-[#f3efe9] bg-muted/30 opacity-60'
                        : 'border-[#f3efe9] hover:border-[#10B981]/30 hover:bg-[#fdfcfb]'
                    }`}
                  >
                    <div
                      className={`h-2 w-2 rounded-full flex-shrink-0 ${statusDotColors[booking.status] || 'bg-slate-300'}`}
                      title={booking.status.replace('_', ' ')}
                    />

                    <div className="flex items-center gap-1.5 w-16 flex-shrink-0">
                      <span className={`text-sm font-semibold ${isNext ? 'text-[#0D4F3C]' : 'text-foreground'}`}>
                        {formatTime(booking.scheduledAt)}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{booking.serviceName}</p>
                      <p className="text-xs text-muted-foreground truncate">{booking.customerName}</p>
                    </div>

                    {isNext && !isPast && (
                      <span className="text-xs font-medium text-[#0D4F3C] bg-[#10B981]/10 px-2 py-0.5 rounded-full whitespace-nowrap">
                        In {minutesUntil}m
                      </span>
                    )}

                    <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </Link>
                );
              })}
            </div>

            {sortedBookings.length > maxVisibleItems && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-2 text-xs text-muted-foreground hover:text-foreground h-8"
                asChild
              >
                <Link to="/operator/calendar" className="flex items-center gap-1">
                  View {sortedBookings.length - maxVisibleItems} more
                  <ChevronRight className="h-3 w-3" />
                </Link>
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// PendingActions
// =============================================================================

export interface PendingAction {
  id: string;
  type: 'no_show' | 'waitlist' | 'payment_failed' | 'review_pending' | 'message_unanswered';
  title: string;
  subtitle: string;
  timestamp: Date | string;
  priority: 'high' | 'medium' | 'low';
  href: string;
}

interface PendingActionsProps {
  actions: PendingAction[];
  isLoading?: boolean;
}

const pendingTypeConfig = {
  no_show: {
    icon: UserX,
    bgColor: 'bg-rose-100',
    iconColor: 'text-rose-600',
  },
  waitlist: {
    icon: Clock,
    bgColor: 'bg-violet-100',
    iconColor: 'text-violet-600',
  },
  payment_failed: {
    icon: CreditCard,
    bgColor: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  review_pending: {
    icon: Star,
    bgColor: 'bg-yellow-100',
    iconColor: 'text-yellow-600',
  },
  message_unanswered: {
    icon: MessageCircle,
    bgColor: 'bg-sky-100',
    iconColor: 'text-sky-600',
  },
};

const priorityConfig = {
  high: 'bg-orange-500 animate-pulse',
  medium: 'bg-amber-400',
  low: 'bg-slate-300',
};

function formatTimeAgo(date: Date | string): string {
  const now = new Date();
  const past = new Date(date);
  const diffMs = now.getTime() - past.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

export function PendingActions({ actions, isLoading }: PendingActionsProps) {
  const groupedActions = {
    high: actions.filter((a) => a.priority === 'high'),
    medium: actions.filter((a) => a.priority === 'medium'),
    low: actions.filter((a) => a.priority === 'low'),
  };

  const sortedActions = [...groupedActions.high, ...groupedActions.medium, ...groupedActions.low];

  const typeCounts = actions.reduce((acc, action) => {
    acc[action.type] = (acc[action.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const highPriorityCount = groupedActions.high.length;

  if (isLoading) {
    return (
      <Card className="border-l-4 border-l-[#f3efe9]">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold font-display flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-muted-foreground" />
            Needs Attention
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-[#f9f7f4] animate-pulse">
                <div className="h-8 w-8 rounded-xl bg-[#f3efe9]" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-[#f3efe9] rounded w-3/4" />
                  <div className="h-3 bg-[#f3efe9] rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (actions.length === 0) {
    return (
      <Card className="border-l-4 border-l-[#10B981]">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold font-display flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-[#10B981]" />
            All Clear
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <div className="h-12 w-12 rounded-full bg-[#10B981]/10 flex items-center justify-center mb-3">
              <CheckCircle2 className="h-6 w-6 text-[#10B981]" />
            </div>
            <p className="text-sm text-muted-foreground">No pending actions. Great job!</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-l-4 border-l-orange-500">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between mb-2">
          {highPriorityCount > 0 && (
            <Badge className="bg-orange-100 text-orange-800 border-0">
              {highPriorityCount} urgent
            </Badge>
          )}
          <div className="flex gap-1 flex-wrap">
            {Object.entries(typeCounts).slice(0, 3).map(([type, count]) => {
              const config = pendingTypeConfig[type as keyof typeof pendingTypeConfig];
              const Icon = config?.icon;
              return Icon ? (
                <span
                  key={type}
                  className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${config.bgColor} ${config.iconColor}`}
                  title={type.replace('_', ' ')}
                >
                  <Icon className="h-3 w-3" />
                  {count}
                </span>
              ) : null;
            })}
          </div>
        </div>
        <CardTitle className="text-lg font-semibold font-display flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-orange-500" />
          Needs Attention
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {sortedActions.slice(0, 5).map((action) => {
            const config = pendingTypeConfig[action.type];
            const Icon = config.icon;
            const isHighPriority = action.priority === 'high';

            return (
              <Link
                key={action.id}
                to={action.href}
                className={`flex items-start gap-3 p-3 rounded-xl transition-colors group ${
                  isHighPriority
                    ? 'bg-rose-50/50 border border-rose-200/60 hover:bg-rose-50'
                    : 'bg-[#f9f7f4] hover:bg-[#f3efe9]'
                }`}
              >
                <div
                  className={`h-2 w-2 rounded-full mt-2.5 ${priorityConfig[action.priority]}`}
                />

                <div
                  className={`h-8 w-8 rounded-xl flex items-center justify-center flex-shrink-0 ${config.bgColor}`}
                >
                  <Icon className={`h-4 w-4 ${config.iconColor}`} />
                </div>

                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${isHighPriority ? 'text-rose-900' : ''}`}>
                    {action.title}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{action.subtitle}</p>
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    {formatTimeAgo(action.timestamp)}
                  </p>
                </div>

                <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-2" />
              </Link>
            );
          })}
        </div>

        {actions.length > 5 && (
          <Button variant="outline" className="w-full mt-4 border-[#0D4F3C]/20" asChild>
            <Link to={"/operator/dashboard/actions" as string}>
              View all {actions.length} items
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// ActivityFeed
// =============================================================================

export interface ActivityItem {
  id: string;
  type: 'booking_created' | 'booking_completed' | 'conversation_started' | 'payment_received' | 'customer_joined' | 'review_received';
  title: string;
  description?: string;
  timestamp: Date | string;
}

interface ActivityFeedProps {
  activities: ActivityItem[];
  isLoading?: boolean;
}

const activityTypeConfig = {
  booking_created: {
    icon: CalendarPlus,
    dotColor: 'bg-[#10B981]',
    iconBg: 'bg-[#10B981]/10',
    iconColor: 'text-[#10B981]',
  },
  booking_completed: {
    icon: CalendarCheck,
    dotColor: 'bg-emerald-500',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
  },
  conversation_started: {
    icon: MessageSquare,
    dotColor: 'bg-sky-500',
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-600',
  },
  payment_received: {
    icon: DollarSign,
    dotColor: 'bg-green-500',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
  },
  customer_joined: {
    icon: UserPlus,
    dotColor: 'bg-violet-500',
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
  },
  review_received: {
    icon: Star,
    dotColor: 'bg-yellow-500',
    iconBg: 'bg-yellow-100',
    iconColor: 'text-yellow-600',
  },
};

function formatFullTimestamp(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ActivityFeed({ activities, isLoading }: ActivityFeedProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold font-display flex items-center gap-2">
            <Activity className="h-5 w-5 text-[#10B981]" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-px bg-[#f3efe9]" />
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="relative flex items-start gap-3 pl-8 animate-pulse">
                  <div className="absolute left-2.5 top-1 h-3 w-3 rounded-full bg-[#f3efe9]" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-[#f3efe9] rounded w-3/4" />
                    <div className="h-3 bg-[#f3efe9] rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (activities.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold font-display flex items-center gap-2">
            <Activity className="h-5 w-5 text-[#10B981]" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Activity className="h-10 w-10 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">No recent activity</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold font-display flex items-center gap-2">
          <Activity className="h-5 w-5 text-[#10B981]" />
          Recent Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-px bg-[#f3efe9]" />

          <div className="space-y-3">
            {activities.slice(0, 6).map((activity) => {
              const config = activityTypeConfig[activity.type];
              const Icon = config.icon;

              return (
                <div
                  key={activity.id}
                  className="animate-activity-slide-in relative flex items-start gap-3 pl-8 group cursor-default"
                >
                  <div
                    className={`absolute left-2.5 top-1.5 h-3 w-3 rounded-full border-2 border-white shadow-sm z-10 ${config.dotColor}`}
                  />

                  <div className={`h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0 ${config.iconBg}`}>
                    <Icon className={`h-3.5 w-3.5 ${config.iconColor}`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium leading-tight">{activity.title}</p>
                    {activity.description && (
                      <p className="text-xs text-muted-foreground truncate">
                        {activity.description}
                      </p>
                    )}
                    <p
                      className="text-[11px] text-muted-foreground/60 mt-0.5 group-hover:hidden"
                    >
                      {formatTimeAgo(activity.timestamp)}
                    </p>
                    <p
                      className="text-[11px] text-muted-foreground/80 mt-0.5 hidden group-hover:block"
                    >
                      {formatFullTimestamp(activity.timestamp)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {activities.length > 6 && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-4 text-muted-foreground hover:text-foreground"
            asChild
          >
            <Link to="/operator/audit-logs" className="flex items-center gap-1">
              View all activity
              <ChevronRight className="h-4 w-4" />
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// RevenueSummary
// =============================================================================

interface RevenueSummaryProps {
  todayRevenue: number;
  yesterdayRevenue: number;
  dailyGoal: number;
  isLoading?: boolean;
}

export function RevenueSummary({
  todayRevenue,
  yesterdayRevenue,
  dailyGoal,
  isLoading = false,
}: RevenueSummaryProps) {
  const percentageChange = yesterdayRevenue > 0
    ? ((todayRevenue - yesterdayRevenue) / yesterdayRevenue) * 100
    : 0;

  const goalPercentage = dailyGoal > 0
    ? Math.min((todayRevenue / dailyGoal) * 100, 100)
    : 0;

  const isPositiveChange = percentageChange >= 0;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (isLoading) {
    return (
      <Card className="relative overflow-hidden bg-gradient-to-br from-[#0D4F3C] to-[#238857]">
        <div className="absolute -right-8 -bottom-8 h-32 w-32 rounded-full bg-white/10" />
        <CardHeader className="pb-2">
          <div className="h-10 w-10 rounded-xl bg-white/20 animate-pulse" />
        </CardHeader>
        <CardContent className="relative space-y-4">
          <div className="h-10 w-40 bg-white/20 rounded animate-pulse" />
          <div className="h-4 w-32 bg-white/20 rounded animate-pulse" />
          <div className="h-2 w-full bg-white/20 rounded-full animate-pulse" />
          <div className="h-3 w-24 bg-white/20 rounded animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="relative overflow-hidden transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg bg-gradient-to-br from-[#0D4F3C] to-[#238857] text-white">
      <div className="absolute -right-8 -bottom-8 h-32 w-32 rounded-full bg-white/10" />

      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-white/20">
          <DollarSign className="h-5 w-5 text-white" />
        </div>
        <div
          className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
            isPositiveChange
              ? 'bg-white/20 text-white'
              : 'bg-orange-400/30 text-orange-100'
          }`}
        >
          {isPositiveChange ? '+' : ''}
          {percentageChange.toFixed(1)}% vs ontem
        </div>
      </CardHeader>

      <CardContent className="relative space-y-4">
        <div>
          <p className="text-sm font-medium text-white/90">Receita de Hoje</p>
          <div className="text-4xl font-bold tracking-tight font-display text-white mt-1">
            {formatCurrency(todayRevenue)}
          </div>
        </div>

        <div className="space-y-2">
          <div className="h-2 w-full bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-500 ease-out"
              style={{ width: `${goalPercentage}%` }}
            />
          </div>
          <p className="text-xs text-white/70">
            {goalPercentage.toFixed(0)}% da meta diaria ({formatCurrency(dailyGoal)})
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// PerformanceChart
// =============================================================================

type Period = '7d' | '30d' | '90d';

interface DataPoint {
  date: string;
  bookings: number;
}

function generateMockData(days: number): DataPoint[] {
  const data: DataPoint[] = [];
  const now = new Date();

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);

    const baseBookings = 8 + Math.floor(Math.random() * 6);
    const dayOfWeek = date.getDay();
    const weekendBonus = dayOfWeek === 0 || dayOfWeek === 6 ? 3 : 0;

    data.push({
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      bookings: baseBookings + weekendBonus,
    });
  }

  return data;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) {
    return null;
  }

  return (
    <div className="bg-white border border-[#f3efe9] rounded-lg shadow-lg p-3">
      <p className="text-sm font-medium text-foreground">{label}</p>
      <p className="text-lg font-bold text-[#0D4F3C]">
        {payload[0].value} bookings
      </p>
    </div>
  );
}

export function PerformanceChart() {
  const [period, setPeriod] = useState<Period>('7d');

  const data = useMemo(() => {
    const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
    return generateMockData(days);
  }, [period]);

  const totalBookings = useMemo(
    () => data.reduce((sum, item) => sum + item.bookings, 0),
    [data]
  );

  const avgBookings = useMemo(
    () => Math.round(totalBookings / data.length),
    [totalBookings, data.length]
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-[#10B981]/10">
            <TrendingUp className="h-5 w-5 text-[#10B981]" />
          </div>
          <div>
            <CardTitle className="text-lg font-semibold font-display">
              Booking Performance
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {totalBookings} total ({avgBookings} avg/day)
            </p>
          </div>
        </div>
        <Tabs value={period} onValueChange={(v) => setPeriod(v as Period)}>
          <TabsList className="bg-[#f3efe9]/50">
            <TabsTrigger
              value="7d"
              className="text-xs data-[state=active]:bg-white data-[state=active]:text-[#0D4F3C]"
            >
              7d
            </TabsTrigger>
            <TabsTrigger
              value="30d"
              className="text-xs data-[state=active]:bg-white data-[state=active]:text-[#0D4F3C]"
            >
              30d
            </TabsTrigger>
            <TabsTrigger
              value="90d"
              className="text-xs data-[state=active]:bg-white data-[state=active]:text-[#0D4F3C]"
            >
              90d
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>
      <CardContent>
        <div className="h-[280px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
            >
              <defs>
                <linearGradient id="bookingsGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#f3efe9"
                vertical={false}
              />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#9ca3af', fontSize: 12 }}
                tickMargin={8}
                interval={period === '7d' ? 0 : period === '30d' ? 4 : 13}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#9ca3af', fontSize: 12 }}
                tickMargin={8}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="bookings"
                stroke="#10B981"
                strokeWidth={2}
                fill="url(#bookingsGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// MobileBottomNav
// =============================================================================

interface NavItem {
  icon: typeof Home;
  label: string;
  href: string;
}

const navItems: NavItem[] = [
  { icon: Home, label: 'Dashboard', href: '/operator' },
  { icon: Calendar, label: 'Calendar', href: '/operator/calendar' },
  { icon: MessageSquare, label: 'Messages', href: '/operator/conversations' },
  { icon: Menu, label: 'Menu', href: '/operator/settings' },
];

export function MobileBottomNav() {
  const location = useLocation();
  const pathname = location.pathname;
  const [menuOpen, setMenuOpen] = useState(false);

  const isActive = (href: string) => {
    if (href === '/operator') {
      return pathname === href || pathname === '/operator/';
    }
    return pathname?.startsWith(href);
  };

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 block sm:hidden bg-white border-t border-[#f3efe9] shadow-lg"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex items-center justify-around h-16 px-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);

            if (item.label === 'Menu') {
              return (
                <button
                  key={item.label}
                  onClick={() => setMenuOpen(!menuOpen)}
                  className={`flex flex-col items-center justify-center flex-1 h-full gap-1 transition-colors ${
                    menuOpen
                      ? 'text-[#0D4F3C]'
                      : 'text-muted-foreground hover:text-[#0D4F3C]'
                  }`}
                  aria-label={item.label}
                >
                  <div className="relative">
                    <Icon className="h-5 w-5" />
                    {menuOpen && (
                      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#10B981]" />
                    )}
                  </div>
                  <span className="text-[10px] font-medium">{item.label}</span>
                </button>
              );
            }

            return (
              <Link
                key={item.label}
                to={item.href}
                className={`flex flex-col items-center justify-center flex-1 h-full gap-1 transition-colors ${
                  active
                    ? 'text-[#0D4F3C]'
                    : 'text-muted-foreground hover:text-[#0D4F3C]'
                }`}
                aria-label={item.label}
              >
                <div className="relative">
                  <Icon className="h-5 w-5" />
                  {active && (
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#10B981]" />
                  )}
                </div>
                <span className="text-[10px] font-medium">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {menuOpen && (
        <div
          className="fixed inset-0 z-40 block sm:hidden bg-black/20 backdrop-blur-sm"
          onClick={() => setMenuOpen(false)}
        >
          <div
            className="absolute bottom-20 left-4 right-4 bg-white rounded-2xl shadow-xl border border-[#f3efe9] overflow-hidden"
            style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-[#f3efe9]">
              <h3 className="font-semibold font-display text-[#0D4F3C]">Menu</h3>
            </div>
            <div className="py-2">
              <Link
                to="/operator/settings"
                className="flex items-center gap-3 px-4 py-3 hover:bg-[#f9f7f4] transition-colors"
                onClick={() => setMenuOpen(false)}
              >
                <div className="h-8 w-8 rounded-xl bg-[#10B981]/10 flex items-center justify-center">
                  <Menu className="h-4 w-4 text-[#10B981]" />
                </div>
                <span className="text-sm font-medium">Settings</span>
              </Link>
              <Link
                to="/operator/bookings"
                className="flex items-center gap-3 px-4 py-3 hover:bg-[#f9f7f4] transition-colors"
                onClick={() => setMenuOpen(false)}
              >
                <div className="h-8 w-8 rounded-xl bg-[#10B981]/10 flex items-center justify-center">
                  <Calendar className="h-4 w-4 text-[#10B981]" />
                </div>
                <span className="text-sm font-medium">Bookings</span>
              </Link>
              <Link
                to="/operator/customers"
                className="flex items-center gap-3 px-4 py-3 hover:bg-[#f9f7f4] transition-colors"
                onClick={() => setMenuOpen(false)}
              >
                <div className="h-8 w-8 rounded-xl bg-[#10B981]/10 flex items-center justify-center">
                  <Home className="h-4 w-4 text-[#10B981]" />
                </div>
                <span className="text-sm font-medium">Customers</span>
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="h-16 block sm:hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} />
    </>
  );
}
